import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'
import { OutlineContext as IOutlineContext, Values } from 'components/Wizards/MultiStepBlogPostWizard/Types'
import { useMutation } from 'react-query'
import { generateIntroduction as generateIntroductionMutationFn } from 'components/Wizards/generateIntroduction'
import { generateOutline as generateOutlineMutationFn } from 'components/Wizards/generateOutline'
import { GeneratePrompt } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'

import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { NewsletterIntroValues } from '../MultiStepEmailNewsletterWizard/SubjectLineSelection/Types'

const OutlineContext = createContext<IOutlineContext>({
  generateIntro: () => Promise.reject('Not implemented'),
  generateOutline: () => Promise.reject('Not implemented'),
  isLoadingOutline: false,
  isLoadingIntro: false,
  introduction: '',
  outline: '',
  abortGenerateIntro: () => {},
  abortGenerateOutline: () => {},
})

export const OutlineProvider = ({ children }: PropsWithChildren) => {
  const workspaceId = useCurrentWorkspaceId()
  const [outline, setOutline] = useState<string>('')
  const [introduction, setIntroduction] = useState<string>('')

  const generateIntroductionMutationAbortController = useRef<AbortController | null>(null)
  if (generateIntroductionMutationAbortController.current === null)
    generateIntroductionMutationAbortController.current = new AbortController()

  const { mutateAsync: generateIntroductionMutateAsync, isLoading: isLoadingIntro } =
    useMutation(generateIntroductionMutationFn)

  const generateOutlineMutationAbortController = useRef<AbortController | null>(null)
  if (generateOutlineMutationAbortController.current === null)
    generateOutlineMutationAbortController.current = new AbortController()

  const { mutateAsync: generateOutlineMutateAsync, isLoading: isLoadingOutline } =
    useMutation(generateOutlineMutationFn)

  const generateIntro = (values: Values | NewsletterIntroValues) => {
    const { title, ...promptValues } = values
    const prompt: GeneratePrompt = { ...promptValues, generation_type: values.generation_type || ChannelType.BlogPost }
    setIntroduction('')
    return generateIntroductionMutateAsync({
      payload: {
        prompt,
        workspaceId,
        title,
      },
      signal: generateIntroductionMutationAbortController.current?.signal,
    }).then((intro) => {
      setIntroduction(intro)
      return intro
    })
  }

  const generateOutline = (values: Values) => {
    const { title, ...promptValues } = values
    const prompt: GeneratePrompt = { ...promptValues, generation_type: ChannelType.BlogPost }
    setOutline('')
    return generateOutlineMutateAsync({
      payload: {
        prompt,
        workspaceId: workspaceId,
        title,
      },
      signal: generateOutlineMutationAbortController.current?.signal,
    }).then((outline) => {
      setOutline(outline)
      return introduction
    })
  }

  return (
    <OutlineContext.Provider
      value={{
        generateIntro,
        abortGenerateIntro: () => {
          generateIntroductionMutationAbortController.current?.abort()
          generateIntroductionMutationAbortController.current = new AbortController()
        },
        generateOutline,
        abortGenerateOutline: () => {
          generateOutlineMutationAbortController.current?.abort()
          generateOutlineMutationAbortController.current = new AbortController()
        },
        isLoadingOutline,
        isLoadingIntro,
        introduction: introduction,
        outline,
      }}
    >
      {children}
    </OutlineContext.Provider>
  )
}

export const useOutline = () => useContext(OutlineContext)
